<template>
    <div class="mirror-page text-light">
        <section class="hero-banner">
            <img src="@/assets/banners/banner-galadriel.webp" alt="El espejo de Galadriel" />
        </section>

        <div class="container py-5">
            <section class="section-box intro-box">
                <div class="section-heading">
                    <span class="section-kicker">OSINT pasivo</span>
                    <h1 class="section-name">El Espejo de Galadriel</h1>
                    <p class="section-copy">
                        Comprueba correo y contraseñas desde el navegador. El correo se analiza con sintaxis, DNS,
                        controles de autenticacion, RDAP y Gravatar. La contraseña se evalua con zxcvbn y HIBP mediante
                        k-anonymity.
                    </p>
                </div>

                <div class="guide-grid">
                    <div class="guide-card">
                        <label>Correo</label>
                        <span>Sintaxis, buzones de rol, proveedor temporal, MX, SPF, DMARC, BIMI, MTA-STS, RDAP y Gravatar.</span>
                    </div>
                    <div class="guide-card">
                        <label>Contrasena</label>
                        <span>Fortaleza local con zxcvbn y consulta HIBP Pwned Passwords por prefijo SHA-1.</span>
                    </div>
                    <div class="guide-card">
                        <label>Privacidad</label>
                        <span>La contraseña completa no se envia: HIBP recibe solo los cinco primeros caracteres del hash SHA-1.</span>
                    </div>
                </div>
            </section>

            <section class="section-box">
                <div class="module-header">
                    <span class="section-kicker">Modulo 01</span>
                    <h2 class="module-title">Comprobación de correo</h2>
                    <p class="module-copy">
                        Valida sintaxis, detecta buzones temporales o de rol, inspecciona MX, SPF, DMARC, BIMI y
                        MTA-STS del dominio, consulta RDAP publico y busca huella publica en Gravatar.
                    </p>
                </div>

                <div class="control-row">
                    <div class="control-field">
                        <label class="field-label" for="email-input">Direccion de correo</label>
                        <input
                            id="email-input"
                            v-model.trim="emailInput"
                            type="email"
                            class="form-control input-dark"
                            placeholder="usuario@dominio.com"
                            @keyup.enter="analyzeEmail"
                        />
                    </div>
                    <button class="btn btn-main action-button" :disabled="emailLoading || !emailInput" @click="analyzeEmail">
                        {{ emailLoading ? "Analizando..." : "Analizar correo" }}
                    </button>
                </div>

                <div v-if="emailLoading" class="loading-panel">
                    <div class="loading-step" v-for="step in emailSteps" :key="step.name" :class="step.state">
                        <div class="loading-step-icon">
                            <span v-if="step.state === 'done'" class="bi bi-check-lg"></span>
                            <span v-else-if="step.state === 'error'" class="bi bi-x-lg"></span>
                            <span v-else-if="step.state === 'active'" class="spinner-border spinner-border-sm"></span>
                            <span v-else class="bi bi-circle"></span>
                        </div>
                        <div class="loading-step-body">
                            <strong>{{ step.name }}</strong>
                            <span>{{ step.status }}</span>
                        </div>
                    </div>
                </div>

                <template v-if="emailResult && !emailLoading">
                    <div class="row g-3 mb-4">
                        <div class="col-6 col-lg-3" v-for="item in emailResult.summaryCards" :key="item.label">
                            <div class="metric-card">
                                <label>{{ item.label }}</label>
                                <span :class="item.tone || scoreClass(item.numeric)">{{ item.value }}</span>
                                <small v-if="item.note">{{ item.note }}</small>
                            </div>
                        </div>
                    </div>

                    <div class="verdict-card" :class="emailResult.verdictTone">
                        <div class="verdict-icon">
                            <span>MAIL</span>
                        </div>
                        <div class="verdict-body">
                            <strong>{{ emailResult.verdictTitle }}</strong>
                            <p>{{ emailResult.verdictBody }}</p>
                        </div>
                    </div>

                    <h5 class="subsection-title">Fuentes consultadas</h5>
                    <div class="row g-3 mb-4">
                        <div class="col-md-6 col-xl-3" v-for="item in emailResult.sourceCards" :key="item.name">
                            <div class="source-card" :class="item.tone">
                                <div class="source-head">
                                    <span>{{ item.name }}</span>
                                    <span class="mini-badge">{{ item.state }}</span>
                                </div>
                                <p>{{ item.description }}</p>
                                <small>{{ item.note }}</small>
                            </div>
                        </div>
                    </div>

                    <h5 class="subsection-title">Senales observadas</h5>
                    <div class="row g-3 mb-4">
                        <div class="col-md-6 col-xl-4" v-for="item in emailResult.signalCards" :key="item.label">
                            <div class="signal-card">
                                <label>{{ item.label }}</label>
                                <span :class="item.tone">{{ item.value }}</span>
                                <small>{{ item.note }}</small>
                            </div>
                        </div>
                    </div>

                    <div class="row g-3">
                        <div class="col-xl-6" v-for="panel in emailResult.rawPanels" :key="panel.title">
                            <div class="tool-card">
                                <div class="card-head">
                                    <h5>{{ panel.title }}</h5>
                                    <span class="mini-badge">{{ panel.badge }}</span>
                                </div>
                                <div class="output-box">
                                    <pre class="result-pre">{{ panel.content }}</pre>
                                </div>
                            </div>
                        </div>
                    </div>
                </template>
            </section>

            <section class="section-box">
                <div class="module-header">
                    <span class="section-kicker">Modulo 02</span>
                    <h2 class="module-title">Comprobación de contraseña</h2>
                    <p class="module-copy">
                        Cruza la contraseña contra HIBP Pwned Passwords sin exponerla completa, calcula fortaleza con
                        zxcvbn y explica patrones, tiempos de crack y mejoras concretas.
                    </p>
                </div>

                <div class="control-row">
                    <div class="control-field">
                        <label class="field-label" for="password-input">Contraseña a revisar</label>
                        <div class="password-shell">
                            <input
                                id="password-input"
                                v-model="passwordInput"
                                :type="showPassword ? 'text' : 'password'"
                                class="form-control input-dark"
                                placeholder="Introduce la contraseña a revisar"
                                @keyup.enter="analyzePassword"
                            />
                            <button type="button" class="password-toggle" @click="showPassword = !showPassword">
                                <i :class="showPassword ? 'bi bi-eye-slash' : 'bi bi-eye'"></i>
                            </button>
                        </div>
                    </div>
                    <button class="btn btn-main action-button" :disabled="passwordLoading || !passwordInput" @click="analyzePassword">
                        {{ passwordLoading ? "Analizando..." : "Analizar contraseña" }}
                    </button>
                </div>

                <div v-if="passwordLoading" class="loading-panel">
                    <div class="loading-step" v-for="step in passwordSteps" :key="step.name" :class="step.state">
                        <div class="loading-step-icon">
                            <span v-if="step.state === 'done'" class="bi bi-check-lg"></span>
                            <span v-else-if="step.state === 'error'" class="bi bi-x-lg"></span>
                            <span v-else-if="step.state === 'active'" class="spinner-border spinner-border-sm"></span>
                            <span v-else class="bi bi-circle"></span>
                        </div>
                        <div class="loading-step-body">
                            <strong>{{ step.name }}</strong>
                            <span>{{ step.status }}</span>
                        </div>
                    </div>
                </div>

                <template v-if="passwordResult && !passwordLoading">
                    <div class="row g-3 mb-4">
                        <div class="col-6 col-lg-3" v-for="item in passwordResult.summaryCards" :key="item.label">
                            <div class="metric-card">
                                <label>{{ item.label }}</label>
                                <span :class="item.tone || scoreClass(item.numeric)">{{ item.value }}</span>
                                <small v-if="item.note">{{ item.note }}</small>
                            </div>
                        </div>
                    </div>

                    <div class="verdict-card" :class="passwordResult.verdictTone">
                        <div class="verdict-icon">
                            <span>PASS</span>
                        </div>
                        <div class="verdict-body">
                            <strong>{{ passwordResult.verdictTitle }}</strong>
                            <p>{{ passwordResult.verdictBody }}</p>
                        </div>
                    </div>

                    <h5 class="subsection-title">Senales observadas</h5>
                    <div class="row g-3 mb-4">
                        <div class="col-md-6 col-xl-4" v-for="item in passwordResult.signalCards" :key="item.label">
                            <div class="signal-card">
                                <label>{{ item.label }}</label>
                                <span :class="item.tone">{{ item.value }}</span>
                                <small>{{ item.note }}</small>
                            </div>
                        </div>
                    </div>

                    <div class="pattern-cloud" v-if="passwordResult.patterns.length">
                        <span class="pattern-chip" v-for="pattern in passwordResult.patterns" :key="pattern">{{ pattern }}</span>
                    </div>

                    <div class="row g-3 mt-1">
                        <div class="col-xl-6" v-for="panel in passwordResult.rawPanels" :key="panel.title">
                            <div class="tool-card">
                                <div class="card-head">
                                    <h5>{{ panel.title }}</h5>
                                    <span class="mini-badge">{{ panel.badge }}</span>
                                </div>
                                <div class="output-box">
                                    <pre class="result-pre">{{ panel.content }}</pre>
                                </div>
                            </div>
                        </div>
                    </div>
                </template>
            </section>

        </div>
    </div>
</template>

<script setup>
import { ref } from 'vue'
import zxcvbn from 'zxcvbn'

const emailInput = ref('')
const emailLoading = ref(false)
const emailResult = ref(null)
const emailSteps = ref([])

const passwordInput = ref('')
const passwordLoading = ref(false)
const passwordResult = ref(null)
const passwordSteps = ref([])
const showPassword = ref(false)

function buildSteps(names) {
    return names.map(name => ({
        name,
        state: 'pending',
        status: 'En espera...'
    }))
}

function updateStep(stepsRef, index, patch) {
    stepsRef.value[index] = {
        ...stepsRef.value[index],
        ...patch
    }
}

function scoreClass(score) {
    if (typeof score !== 'number') return 'tone-neutral'
    if (score >= 80) return 'tone-success'
    if (score >= 55) return 'tone-warning'
    return 'tone-danger'
}

function verdictTone(score) {
    if (typeof score !== 'number') return 'tone-neutral'
    if (score >= 80) return 'verdict-success'
    if (score >= 55) return 'verdict-warning'
    return 'verdict-danger'
}

function uniqueList(items) {
    return [...new Set(items.filter(Boolean))]
}

function safeJsonParse(text) {
    return JSON.parse(String(text).trim())
}

function prettyPrint(value) {
    if (value === null || value === undefined || value === '') return 'Sin datos.'
    if (typeof value === 'string') return value
    try {
        return JSON.stringify(value, null, 2)
    } catch (error) {
        return String(value)
    }
}

function normalizeTxtRecord(value) {
    return String(value).replace(/^"+|"+$/g, '').replace(/"\s*"/g, '')
}

function formatDate(value) {
    if (!value) return 'No disponible'
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return String(value)
    return date.toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
    })
}

function formatDateTime(value) {
    if (!value) return 'No disponible'
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return String(value)
    return date.toLocaleString('es-ES', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    })
}

function hostFromEmail(email) {
    return String(email).split('@')[1]?.toLowerCase() || ''
}

async function sha1Hex(value) {
    const buffer = await crypto.subtle.digest('SHA-1', new TextEncoder().encode(value))
    return Array.from(new Uint8Array(buffer))
        .map(byte => byte.toString(16).padStart(2, '0'))
        .join('')
        .toUpperCase()
}

async function sha256Hex(value) {
    const buffer = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value))
    return Array.from(new Uint8Array(buffer))
        .map(byte => byte.toString(16).padStart(2, '0'))
        .join('')
}

async function fetchTextDirect(url, options = {}) {
    const response = await fetch(url, options)
    if (!response.ok) throw new Error(`HTTP ${response.status}`)
    return await response.text()
}

async function fetchTextViaProxy(url) {
    const proxies = [
        `https://api.allorigins.win/raw?url=${encodeURIComponent(url)}`,
        `https://api.codetabs.com/v1/proxy?quest=${encodeURIComponent(url)}`
    ]

    let lastError = null
    for (const proxyUrl of proxies) {
        try {
            const response = await fetch(proxyUrl)
            if (!response.ok) throw new Error(`HTTP ${response.status}`)
            return await response.text()
        } catch (error) {
            lastError = error
        }
    }

    throw lastError || new Error('No se pudo recuperar el recurso.')
}

async function fetchTextSmart(url, options = {}) {
    const method = (options.method || 'GET').toUpperCase()
    if (method !== 'GET') {
        return await fetchTextDirect(url, options)
    }

    try {
        return await fetchTextDirect(url, options)
    } catch (error) {
        return await fetchTextViaProxy(url)
    }
}

async function fetchJsonSmart(url, options = {}) {
    const text = await fetchTextSmart(url, options)
    return safeJsonParse(text)
}

async function fetchPublicText(url) {
    try {
        const content = await fetchTextSmart(url)
        return { success: true, content }
    } catch (error) {
        return { success: false, content: '', error: error.message }
    }
}

async function fetchDnsRecords(name, type) {
    try {
        const json = await fetchJsonSmart(`https://dns.google/resolve?name=${encodeURIComponent(name)}&type=${type}`)
        const records = (json.Answer || []).map(answer => {
            return type === 'TXT' ? normalizeTxtRecord(answer.data) : answer.data
        })
        return { success: true, records, raw: json, status: json.Status }
    } catch (error) {
        return { success: false, records: [], raw: null, error: error.message }
    }
}

async function fetchEmailValidation(email) {
    try {
        const json = await fetchJsonSmart(`https://api.eva.pingutil.com/email?email=${encodeURIComponent(email)}`)
        const data = json.data || json
        return {
            success: true,
            valid: data.valid_syntax ?? data.valid ?? /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email),
            disposable: data.disposable ?? false,
            role: data.role_account ?? data.role ?? false,
            deliverable: data.deliverable ?? null,
            domain: data.domain || hostFromEmail(email)
        }
    } catch (error) {
        return {
            success: false,
            valid: /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email),
            disposable: false,
            role: false,
            deliverable: null,
            domain: hostFromEmail(email),
            error: error.message
        }
    }
}

async function fetchGravatarProfile(email) {
    const hash = await sha256Hex(email.toLowerCase().trim())
    const profileUrl = `https://www.gravatar.com/${hash}.json`

    try {
        const response = await fetch(profileUrl)
        if (response.status === 404) {
            return { success: true, hasProfile: false, hash }
        }
        if (!response.ok) throw new Error(`HTTP ${response.status}`)
        const json = await response.json()
        const entry = json.entry?.[0]
        return {
            success: true,
            hasProfile: Boolean(entry),
            hash,
            raw: json,
            displayName: entry?.displayName || entry?.preferredUsername || 'Sin alias visible',
            profileUrl: entry?.profileUrl || '',
            thumbnail: entry?.thumbnailUrl || '',
            location: entry?.currentLocation || ''
        }
    } catch (directError) {
        try {
            const json = await fetchJsonSmart(profileUrl)
            const entry = json.entry?.[0]
            return {
                success: true,
                hasProfile: Boolean(entry),
                hash,
                raw: json,
                displayName: entry?.displayName || entry?.preferredUsername || 'Sin alias visible',
                profileUrl: entry?.profileUrl || '',
                thumbnail: entry?.thumbnailUrl || '',
                location: entry?.currentLocation || ''
            }
        } catch (error) {
            return {
                success: false,
                hasProfile: false,
                hash,
                error: error.message
            }
        }
    }
}

function readVCardField(entity, fieldName) {
    const entries = entity?.vcardArray?.[1] || []
    const match = entries.find(entry => entry[0] === fieldName)
    return match?.[3] || ''
}

function findRegistrarName(entities = []) {
    const registrar = entities.find(entity => entity.roles?.includes('registrar'))
    return readVCardField(registrar, 'fn') || registrar?.handle || 'No disponible'
}

function findEventDate(events = [], eventAction) {
    return events.find(event => event.eventAction === eventAction)?.eventDate || ''
}

async function fetchDomainRdap(domain) {
    try {
        const raw = await fetchJsonSmart(`https://rdap.org/domain/${encodeURIComponent(domain)}`)
        return {
            success: true,
            raw,
            registrar: findRegistrarName(raw.entities),
            created: findEventDate(raw.events, 'registration'),
            expires: findEventDate(raw.events, 'expiration'),
            updated: findEventDate(raw.events, 'last changed'),
            status: raw.status || [],
            nameservers: (raw.nameservers || []).map(item => item.ldhName)
        }
    } catch (error) {
        return {
            success: false,
            raw: null,
            registrar: 'No disponible',
            created: '',
            expires: '',
            updated: '',
            status: [],
            nameservers: [],
            error: error.message
        }
    }
}

async function fetchMailDomainSignals(domain) {
    const [mx, txt, dmarcTxt, bimiTxt, mtaStsTxt, mtaStsPolicy] = await Promise.all([
        fetchDnsRecords(domain, 'MX'),
        fetchDnsRecords(domain, 'TXT'),
        fetchDnsRecords(`_dmarc.${domain}`, 'TXT'),
        fetchDnsRecords(`default._bimi.${domain}`, 'TXT'),
        fetchDnsRecords(`_mta-sts.${domain}`, 'TXT'),
        fetchPublicText(`https://mta-sts.${domain}/.well-known/mta-sts.txt`)
    ])

    const spf = txt.records.find(record => record.toLowerCase().startsWith('v=spf1')) || ''
    const dmarc = dmarcTxt.records.find(record => record.toLowerCase().startsWith('v=dmarc1')) || ''
    const bimi = bimiTxt.records.find(record => record.toLowerCase().startsWith('v=bimi1')) || ''
    const mtaSts = mtaStsTxt.records.find(record => record.toLowerCase().startsWith('v=stsv1')) || ''

    return {
        mx,
        txt,
        dmarcTxt,
        bimiTxt,
        mtaStsTxt,
        mtaStsPolicy,
        spf,
        dmarc,
        bimi,
        mtaSts
    }
}

async function queryPwnedPasswords(password) {
    const hex = await sha1Hex(password)
    const prefix = hex.slice(0, 5)
    const suffix = hex.slice(5)
    const response = await fetch(`https://api.pwnedpasswords.com/range/${prefix}`, {
        headers: { 'Add-Padding': 'true' }
    })
    if (!response.ok) throw new Error(`HTTP ${response.status}`)
    const body = await response.text()
    const match = body
        .split('\n')
        .map(line => line.trim())
        .find(line => line.toUpperCase().startsWith(suffix))
    const count = match ? parseInt(match.split(':')[1], 10) : 0
    return {
        count,
        prefix,
        hashSuffixMatched: Boolean(match)
    }
}

function shortList(records, max = 3) {
    if (!records?.length) return 'Sin datos'
    return records.slice(0, max).join(' | ')
}

async function analyzeEmail() {
    if (!emailInput.value) return

    const email = emailInput.value.trim().toLowerCase()
    const domain = hostFromEmail(email)
    if (!domain) {
        window.alert('Introduce un correo valido.')
        return
    }

    emailLoading.value = true
    emailResult.value = null
    emailSteps.value = buildSteps([
        'Validacion base',
        'Controles del dominio de correo',
        'Huella publica en Gravatar',
        'Registro RDAP del dominio'
    ])

    try {
        updateStep(emailSteps, 0, { state: 'active', status: 'Consultando EVA Pingutil...' })
        const validation = await fetchEmailValidation(email)
        updateStep(emailSteps, 0, {
            state: validation.success ? 'done' : 'error',
            status: validation.success ? 'Sintaxis y flags recibidos.' : `Fallback local: ${validation.error}`
        })

        updateStep(emailSteps, 1, { state: 'active', status: 'Consultando MX, TXT, DMARC, BIMI y MTA-STS...' })
        const mailSignals = await fetchMailDomainSignals(domain)
        const mailSignalSuccess = mailSignals.mx.success || mailSignals.txt.success || mailSignals.dmarcTxt.success
        updateStep(emailSteps, 1, {
            state: mailSignalSuccess ? 'done' : 'error',
            status: mailSignalSuccess ? 'Politicas y transporte de correo recopilados.' : 'No se pudieron leer los registros DNS.'
        })

        updateStep(emailSteps, 2, { state: 'active', status: 'Buscando perfil publico en Gravatar...' })
        const gravatar = await fetchGravatarProfile(email)
        updateStep(emailSteps, 2, {
            state: gravatar.success ? 'done' : 'error',
            status: gravatar.success
                ? (gravatar.hasProfile ? 'Perfil publico encontrado.' : 'Sin perfil publico visible.')
                : `No disponible: ${gravatar.error}`
        })

        updateStep(emailSteps, 3, { state: 'active', status: 'Consultando RDAP del dominio...' })
        const rdap = await fetchDomainRdap(domain)
        updateStep(emailSteps, 3, {
            state: rdap.success ? 'done' : 'error',
            status: rdap.success ? 'Registro del dominio obtenido.' : `No disponible: ${rdap.error}`
        })

        const hasMx = mailSignals.mx.records.length > 0
        const hasSpf = Boolean(mailSignals.spf)
        const hasDmarc = Boolean(mailSignals.dmarc)
        const hasMtaSts = Boolean(mailSignals.mtaSts)
        const hasBimi = Boolean(mailSignals.bimi)

        let score = 100
        if (!validation.valid) score -= 35
        if (validation.disposable) score -= 25
        if (validation.role) score -= 10
        if (!hasMx) score -= 25
        if (!hasSpf) score -= 10
        if (!hasDmarc) score -= 15
        if (!hasMtaSts) score -= 8
        if (validation.deliverable === false) score -= 10
        score = Math.max(0, Math.min(100, Math.round(score)))

        const antiSpoofing = hasSpf && hasDmarc
            ? 'SPF y DMARC presentes'
            : hasSpf || hasDmarc
                ? 'Solo un control visible'
                : 'Sin blindaje antisuplantacion'

        const verdictTitle = score >= 80
            ? 'Correo con infraestructura razonablemente sana'
            : score >= 55
                ? 'Correo util, pero con blindaje parcial'
                : 'Correo con senales de riesgo o controles insuficientes'

        const verdictBody = antiSpoofing === 'SPF y DMARC presentes'
            ? 'La superficie de correo publica muestra controles antisuplantacion visibles y una topologia de entrega reconocible.'
            : 'Faltan controles que ayuden a reducir suplantacion, degradacion SMTP o abuso de la identidad del dominio.'

        emailResult.value = {
            score,
            verdictTone: verdictTone(score),
            verdictTitle,
            verdictBody,
            summaryCards: [
                {
                    label: 'Puntuacion',
                    value: `${score}/100`,
                    numeric: score,
                    note: domain
                },
                {
                    label: 'Sintaxis',
                    value: validation.valid ? 'Valida' : 'Dudosa',
                    tone: validation.valid ? 'tone-success' : 'tone-danger',
                    note: validation.deliverable === null ? 'Entrega no confirmada' : (validation.deliverable ? 'Entrega plausible' : 'Entrega dudosa')
                },
                {
                    label: 'Blindaje de correo',
                    value: antiSpoofing,
                    tone: antiSpoofing === 'SPF y DMARC presentes' ? 'tone-success' : 'tone-warning',
                    note: hasMtaSts ? 'MTA-STS visible' : 'MTA-STS no visible'
                },
                {
                    label: 'MX visibles',
                    value: hasMx ? String(mailSignals.mx.records.length) : '0',
                    tone: hasMx ? 'tone-success' : 'tone-danger',
                    note: shortList(mailSignals.mx.records, 2)
                }
            ],
            sourceCards: [
                {
                    name: 'EVA Pingutil',
                    state: validation.success ? 'Exitoso' : 'Fallback',
                    tone: validation.success ? 'tone-success' : 'tone-warning',
                    description: 'Validacion sintactica, cuentas temporales, buzones de rol y entrega probable.',
                    note: validation.success ? 'Respuesta publica procesada.' : (validation.error || 'Solo se uso regex local.')
                },
                {
                    name: 'DNS over HTTPS',
                    state: mailSignalSuccess ? 'Exitoso' : 'Fallido',
                    tone: mailSignalSuccess ? 'tone-success' : 'tone-danger',
                    description: 'Consulta de MX, TXT, SPF, DMARC, BIMI y MTA-STS desde dns.google.',
                    note: hasMx ? `MX: ${shortList(mailSignals.mx.records, 2)}` : 'Sin MX visibles.'
                },
                {
                    name: 'Gravatar',
                    state: gravatar.success ? (gravatar.hasProfile ? 'Perfil visible' : 'Sin perfil') : 'No disponible',
                    tone: gravatar.success ? (gravatar.hasProfile ? 'tone-success' : 'tone-warning') : 'tone-danger',
                    description: 'Busqueda de huella publica y alias expuestos a partir del hash SHA-256 del correo.',
                    note: gravatar.success
                        ? (gravatar.hasProfile ? `${gravatar.displayName}${gravatar.location ? ` · ${gravatar.location}` : ''}` : 'No se encontro perfil publico.')
                        : gravatar.error
                },
                {
                    name: 'RDAP',
                    state: rdap.success ? 'Exitoso' : 'No disponible',
                    tone: rdap.success ? 'tone-success' : 'tone-warning',
                    description: 'Registro publico del dominio: alta, expiracion, registrar y nameservers.',
                    note: rdap.success
                        ? `Alta: ${formatDate(rdap.created)} · Expira: ${formatDate(rdap.expires)}`
                        : (rdap.error || 'Sin respuesta util.')
                }
            ],
            signalCards: [
                {
                    label: 'Cuenta temporal',
                    value: validation.disposable ? 'Si' : 'No',
                    tone: validation.disposable ? 'tone-danger' : 'tone-success',
                    note: validation.disposable ? 'Los buzones desechables suelen elevar riesgo operativo.' : 'No se detecta proveedor temporal.'
                },
                {
                    label: 'Buzon de rol',
                    value: validation.role ? 'Si' : 'No',
                    tone: validation.role ? 'tone-warning' : 'tone-success',
                    note: validation.role ? 'Alias generico con mas superficie de abuso.' : 'Parece una cuenta personal o nominal.'
                },
                {
                    label: 'SPF',
                    value: hasSpf ? 'Visible' : 'Ausente',
                    tone: hasSpf ? 'tone-success' : 'tone-danger',
                    note: hasSpf ? mailSignals.spf : 'Sin SPF publico en TXT raiz.'
                },
                {
                    label: 'DMARC',
                    value: hasDmarc ? 'Visible' : 'Ausente',
                    tone: hasDmarc ? 'tone-success' : 'tone-danger',
                    note: hasDmarc ? mailSignals.dmarc : 'No hay politica DMARC publicada.'
                },
                {
                    label: 'BIMI',
                    value: hasBimi ? 'Visible' : 'No visible',
                    tone: hasBimi ? 'tone-success' : 'tone-neutral',
                    note: hasBimi ? mailSignals.bimi : 'BIMI es opcional y aqui no aparece.'
                },
                {
                    label: 'MTA-STS',
                    value: hasMtaSts ? 'TXT visible' : 'No visible',
                    tone: hasMtaSts ? 'tone-success' : 'tone-warning',
                    note: mailSignals.mtaStsPolicy.success
                        ? `Politica recuperada desde ${domain}.`
                        : 'No se pudo leer la politica en /.well-known/mta-sts.txt.'
                },
                {
                    label: 'Registrar',
                    value: rdap.registrar,
                    tone: rdap.success ? 'tone-neutral' : 'tone-warning',
                    note: rdap.success ? `Nameservers: ${rdap.nameservers.length}` : 'Sin respuesta RDAP consistente.'
                },
                {
                    label: 'Huella publica',
                    value: gravatar.hasProfile ? 'Perfil visible' : 'Sin perfil',
                    tone: gravatar.hasProfile ? 'tone-warning' : 'tone-success',
                    note: gravatar.hasProfile ? 'Existe correlacion publica del correo en Gravatar.' : 'No se observa perfil publico en este origen.'
                }
            ],
            rawPanels: [
                {
                    title: 'TXT y controles de correo',
                    badge: 'SPF / DMARC / BIMI / MTA-STS',
                    content: prettyPrint({
                        mx: mailSignals.mx.records,
                        txt: mailSignals.txt.records,
                        spf: mailSignals.spf || 'No visible',
                        dmarc: mailSignals.dmarc || 'No visible',
                        bimi: mailSignals.bimi || 'No visible',
                        mta_sts_txt: mailSignals.mtaSts || 'No visible',
                        mta_sts_policy: mailSignals.mtaStsPolicy.success
                            ? mailSignals.mtaStsPolicy.content
                            : (mailSignals.mtaStsPolicy.error || 'No disponible')
                    })
                },
                {
                    title: 'RDAP del dominio',
                    badge: 'Registro publico',
                    content: prettyPrint(
                        rdap.success
                            ? {
                                registrar: rdap.registrar,
                                created: rdap.created,
                                expires: rdap.expires,
                                updated: rdap.updated,
                                status: rdap.status,
                                nameservers: rdap.nameservers
                            }
                            : { error: rdap.error || 'Sin respuesta util.' }
                    )
                }
            ]
        }
    } finally {
        emailLoading.value = false
    }
}

async function analyzePassword() {
    if (!passwordInput.value) return

    passwordLoading.value = true
    passwordResult.value = null
    passwordSteps.value = buildSteps([
        'Analisis local con zxcvbn',
        'Consulta HIBP Pwned Passwords'
    ])

    try {
        updateStep(passwordSteps, 0, { state: 'active', status: 'Calculando patrones, longitud y complejidad...' })
        const strength = zxcvbn(passwordInput.value)
        updateStep(passwordSteps, 0, { state: 'done', status: 'Modelo local completado.' })

        updateStep(passwordSteps, 1, { state: 'active', status: 'Enviando solo el prefijo SHA-1 a HIBP...' })
        let pwned
        try {
            pwned = await queryPwnedPasswords(passwordInput.value)
            updateStep(passwordSteps, 1, {
                state: 'done',
                status: pwned.count > 0
                    ? `Coincidencia encontrada: ${pwned.count.toLocaleString()} exposiciones.`
                    : 'Sin coincidencias en HIBP.'
            })
        } catch (error) {
            pwned = { count: 0, error: error.message, prefix: 'N/D', hashSuffixMatched: false }
            updateStep(passwordSteps, 1, { state: 'error', status: `Error: ${error.message}` })
        }

        const baseScoreMap = [16, 36, 58, 79, 93]
        let score = baseScoreMap[strength.score] ?? 20
        if (pwned.count > 0) score = Math.min(score, pwned.count > 1000 ? 8 : pwned.count > 10 ? 20 : 32)
        score = Math.max(0, Math.min(100, Math.round(score)))

        const feedback = uniqueList([
            strength.feedback.warning,
            ...(strength.feedback.suggestions || [])
        ])

        const patterns = strength.sequence
            .slice(0, 8)
            .map(sequence => `${translatePattern(sequence.pattern)}: ${sequence.token}`)

        const crackTime =
            strength.crack_times_display?.offline_slow_hashing_1e4_per_second ||
            strength.crack_times_display?.offline_fast_hashing_1e10_per_second ||
            'No disponible'

        const verdictTitle = pwned.count > 0
            ? 'Contraseña comprometida en brechas reales'
            : score >= 80
                ? 'Contraseña robusta y sin coincidencias en HIBP'
                : score >= 55
                    ? 'Contraseña razonable, pero mejorable'
                    : 'Contraseña débil o predecible'

        const verdictBody = pwned.count > 0
            ? `Se ha observado en HIBP ${pwned.count.toLocaleString()} veces. Aunque su forma parezca compleja, ya no puede considerarse secreta.`
            : `zxcvbn la clasifica con ${strength.score}/4 y estima tiempos de crack muy variables segun el atacante y el hash.`

        passwordResult.value = {
            score,
            verdictTone: verdictTone(score),
            verdictTitle,
            verdictBody,
            patterns,
            summaryCards: [
                {
                    label: 'Puntuacion',
                    value: `${score}/100`,
                    numeric: score,
                    note: `zxcvbn ${strength.score}/4`
                },
                {
                    label: 'Estado HIBP',
                    value: pwned.count > 0 ? 'Expuesta' : 'No encontrada',
                    tone: pwned.count > 0 ? 'tone-danger' : 'tone-success',
                    note: pwned.error || 'Consulta por rango SHA-1'
                },
                {
                    label: 'Apariciones',
                    value: pwned.count.toLocaleString(),
                    tone: pwned.count > 0 ? 'tone-danger' : 'tone-success',
                    note: 'Coincidencias en brechas publicas'
                },
                {
                    label: 'Crack offline',
                    value: crackTime,
                    tone: score >= 80 ? 'tone-success' : score >= 55 ? 'tone-warning' : 'tone-danger',
                    note: 'Modelo lento 1e4 hashes/seg'
                }
            ],
            signalCards: [
                {
                    label: 'Longitud',
                    value: `${passwordInput.value.length} caracteres`,
                    tone: passwordInput.value.length >= 14 ? 'tone-success' : 'tone-warning',
                    note: passwordInput.value.length >= 16 ? 'Muy buena base estructural.' : 'La longitud sigue siendo el multiplicador mas rentable.'
                },
                {
                    label: 'Diversidad',
                    value: describeAlphabet(passwordInput.value),
                    tone: /[^A-Za-z0-9]/.test(passwordInput.value) && /[A-Z]/.test(passwordInput.value) && /[0-9]/.test(passwordInput.value)
                        ? 'tone-success'
                        : 'tone-warning',
                    note: 'Mayusculas, minusculas, numeros y simbolos elevan cobertura del espacio de claves.'
                },
                {
                    label: 'Guesses log10',
                    value: strength.guesses_log10.toFixed(2),
                    tone: strength.guesses_log10 >= 10 ? 'tone-success' : strength.guesses_log10 >= 7 ? 'tone-warning' : 'tone-danger',
                    note: 'Estimacion logaritmica de intentos necesarios.'
                },
                {
                    label: 'Patrones detectados',
                    value: patterns.length ? String(patterns.length) : '0',
                    tone: patterns.length > 2 ? 'tone-warning' : 'tone-success',
                    note: patterns.length ? patterns.slice(0, 2).join(' | ') : 'No se han detectado patrones obvios.'
                },
                {
                    label: 'Warning principal',
                    value: strength.feedback.warning || 'Sin warning fuerte',
                    tone: strength.feedback.warning ? 'tone-warning' : 'tone-success',
                    note: feedback.join(' | ') || 'Sin sugerencias adicionales.'
                },
                {
                    label: 'Consulta k-anonymity',
                    value: pwned.prefix,
                    tone: 'tone-neutral',
                    note: 'Solo el prefijo SHA-1 viaja al servicio remoto.'
                }
            ],
            rawPanels: [
                {
                    title: 'Lectura tecnica zxcvbn',
                    badge: 'Analisis local',
                    content: prettyPrint({
                        score: strength.score,
                        guesses: strength.guesses,
                        guesses_log10: strength.guesses_log10,
                        crack_times_display: strength.crack_times_display,
                        warning: strength.feedback.warning,
                        suggestions: strength.feedback.suggestions,
                        sequence: strength.sequence.map(sequence => ({
                            pattern: sequence.pattern,
                            token: sequence.token,
                            guesses: sequence.guesses
                        }))
                    })
                },
                {
                    title: 'Consulta HIBP Pwned Passwords',
                    badge: 'Rango SHA-1',
                    content: prettyPrint({
                        prefix_sent: pwned.prefix,
                        exposed_count: pwned.count,
                        suffix_match: pwned.hashSuffixMatched,
                        error: pwned.error || null
                    })
                }
            ]
        }
    } finally {
        passwordLoading.value = false
    }
}

function translatePattern(pattern) {
    const labels = {
        dictionary: 'Diccionario',
        spatial: 'Patron de teclado',
        repeat: 'Repeticion',
        sequence: 'Secuencia',
        regex: 'Regex',
        date: 'Fecha',
        bruteforce: 'Fuerza bruta'
    }
    return labels[pattern] || pattern
}

function describeAlphabet(password) {
    const parts = []
    if (/[a-z]/.test(password)) parts.push('minusculas')
    if (/[A-Z]/.test(password)) parts.push('mayusculas')
    if (/[0-9]/.test(password)) parts.push('numeros')
    if (/[^A-Za-z0-9]/.test(password)) parts.push('simbolos')
    return parts.length ? parts.join(' + ') : 'alfabeto minimo'
}
</script>

<style scoped>
.mirror-page {
    background: #0b0f17;
    color: #e6edf3;
    min-height: 100vh;
    font-family: 'Inter', sans-serif;
}

.hero-banner {
    margin-top: 53px;
}

.hero-banner img {
    width: 100%;
    display: block;
    object-fit: cover;
}

.section-box {
    background: #111827;
    border: 1px solid #1f2937;
    border-radius: 10px;
    padding: 24px;
    margin-bottom: 24px;
}

.intro-box,
.module-header {
    display: grid;
    gap: 10px;
}

.section-heading {
    display: grid;
    gap: 12px;
}

.section-kicker {
    color: #94a3b8;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.18em;
    text-transform: uppercase;
}

.section-name,
.module-title {
    margin: 0;
    color: #f8fafc;
    font-size: clamp(1.45rem, 2.1vw, 2rem);
    font-weight: 700;
}

.section-copy,
.module-copy {
    margin: 0;
    color: #9ca3af;
    line-height: 1.7;
    max-width: 960px;
}

.guide-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 16px;
    margin-top: 8px;
}

.guide-card,
.tool-card,
.source-card,
.signal-card,
.metric-card {
    background: #020617;
    border: 1px solid #1e293b;
    border-radius: 8px;
}

.guide-card {
    padding: 16px;
    display: grid;
    gap: 6px;
}

.guide-card label,
.field-label,
.metric-card label,
.signal-card label {
    color: #9ca3af;
    font-size: 0.78rem;
    font-weight: 600;
    letter-spacing: 0.03em;
    display: block;
    margin-bottom: 6px;
}

.guide-card span {
    color: #dbe4ee;
    line-height: 1.6;
}

.control-row {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 16px;
    align-items: end;
    margin: 20px 0 18px;
}

.control-field {
    min-width: 0;
}

.input-dark {
    background: #020617;
    border: 1px solid #334155;
    color: #f8fafc !important;
    min-height: 46px;
    caret-color: #f8fafc;
}

.input-dark::placeholder {
    color: #64748b;
}

.input-dark:focus {
    background: #020617;
    border-color: #475569;
    box-shadow: 0 0 0 0.2rem rgba(71, 85, 105, 0.2);
}

.action-button {
    min-width: 180px;
    height: 46px;
}

.btn-main {
    background: #334155;
    color: #f8fafc;
    border: 1px solid #475569;
    border-radius: 8px;
    padding: 10px 16px;
    font-weight: 600;
}

.btn-main:hover,
.btn-main:focus {
    background: #475569;
    color: #f8fafc;
}

.btn-main:disabled {
    opacity: 0.65;
    cursor: var(--cursor-not-allowed), not-allowed;
}

.password-shell {
    position: relative;
}

.password-toggle {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    border: 0;
    background: transparent;
    color: #94a3b8;
}

.metric-card,
.signal-card {
    padding: 14px;
    height: 100%;
    display: grid;
    gap: 4px;
}

.metric-card span,
.signal-card span {
    color: #f8fafc;
    font-size: 1rem;
    font-weight: 600;
    word-break: break-word;
}

.metric-card small,
.signal-card small,
.source-card small {
    color: #94a3b8;
    line-height: 1.5;
}

.subsection-title {
    margin: 24px 0 14px;
    color: #e2e8f0;
    font-size: 0.92rem;
    font-weight: 700;
    letter-spacing: 0.14em;
    text-transform: uppercase;
}

.source-card {
    padding: 16px;
    height: 100%;
    display: grid;
    gap: 8px;
}

.source-card.tone-success {
    border-color: rgba(34, 197, 94, 0.28);
}

.source-card.tone-warning {
    border-color: rgba(245, 158, 11, 0.28);
}

.source-card.tone-danger {
    border-color: rgba(248, 113, 113, 0.28);
}

.source-head {
    display: flex;
    justify-content: space-between;
    gap: 10px;
    align-items: center;
}

.source-head span:first-child {
    color: #f8fafc;
    font-weight: 600;
}

.source-card p {
    margin: 0;
    color: #d6dde7;
    line-height: 1.6;
    font-size: 0.9rem;
}

.mini-badge {
    background: rgba(71, 85, 105, 0.35);
    border: 1px solid #334155;
    border-radius: 999px;
    color: #cbd5e1;
    font-size: 0.72rem;
    padding: 4px 10px;
    white-space: nowrap;
}

.verdict-card {
    display: flex;
    gap: 14px;
    border-radius: 10px;
    padding: 16px 18px;
    border: 1px solid rgba(148, 163, 184, 0.22);
    margin-bottom: 18px;
}

.verdict-icon {
    width: 42px;
    height: 42px;
    border-radius: 999px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(15, 23, 42, 0.8);
    color: #e2e8f0;
    flex-shrink: 0;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
}

.verdict-body strong {
    display: block;
    color: #f8fafc;
    margin-bottom: 4px;
}

.verdict-body p {
    margin: 0;
    color: #c9d4df;
    line-height: 1.6;
}

.verdict-success {
    background: rgba(22, 101, 52, 0.14);
    border-color: rgba(34, 197, 94, 0.3);
}

.verdict-warning {
    background: rgba(146, 64, 14, 0.14);
    border-color: rgba(245, 158, 11, 0.3);
}

.verdict-danger {
    background: rgba(127, 29, 29, 0.14);
    border-color: rgba(248, 113, 113, 0.3);
}

.tool-card {
    padding: 16px;
    display: grid;
    gap: 12px;
    height: 100%;
}

.card-head {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    align-items: center;
}

.card-head h5 {
    margin: 0;
    color: #f8fafc;
    font-size: 1rem;
    font-weight: 600;
}

.output-box {
    background: #020617;
    border: 1px solid #1e293b;
    border-radius: 8px;
    padding: 14px;
    max-height: 420px;
    overflow: auto;
    max-width: 100%;
    -webkit-overflow-scrolling: touch;
}

.result-pre {
    margin: 0;
    color: #cbd5e1;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
    word-break: break-word;
    font-size: 0.84rem;
    line-height: 1.6;
    font-family: 'Courier New', monospace;
}

.pattern-cloud {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin: 6px 0 2px;
}

.pattern-chip {
    background: rgba(51, 65, 85, 0.45);
    border: 1px solid #334155;
    border-radius: 999px;
    color: #dbe7f3;
    font-size: 0.8rem;
    padding: 6px 10px;
}

.tone-success {
    color: #86efac !important;
    stroke: #22c55e;
}

.tone-warning {
    color: #fcd34d !important;
    stroke: #f59e0b;
}

.tone-danger {
    color: #fca5a5 !important;
    stroke: #ef4444;
}

.tone-neutral {
    color: #cbd5e1 !important;
    stroke: #94a3b8;
}

@media (max-width: 1199px) {
    .guide-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}

@media (max-width: 767px) {
    .section-box {
        padding: 16px;
    }

    .guide-grid {
        grid-template-columns: 1fr;
    }

    .control-row {
        grid-template-columns: 1fr;
    }

    .action-button {
        width: 100%;
        min-width: 0;
    }

    .card-head {
        flex-direction: column;
        align-items: flex-start;
    }

    .source-head,
    .verdict-card {
        flex-direction: column;
        align-items: flex-start;
    }

    .mini-badge {
        white-space: normal;
        text-align: center;
        overflow-wrap: anywhere;
    }

    .result-pre {
        font-size: 0.78rem;
    }
}

@media (max-width: 420px) {
    .section-box {
        padding: 14px;
        border-radius: 8px;
    }

    .section-kicker {
        letter-spacing: 0.08em;
    }
}
</style>
